/**
 * Ảnh đi vào ô ảnh bằng kéo thả hoặc dán, không chỉ qua hộp chọn tệp.
 *
 * Roary 09/10/2026: nhập sản phẩm phải lưu ảnh WeChat ra máy rồi chọn lại trong hộp tệp; muốn kéo ảnh từ WeChat thả vào,
 * hoặc copy ảnh rồi dán. Phần thuần này chỉ đọc `DataTransfer` (của sự kiện drop / paste) qua một hình dạng tối thiểu, để
 * test được trong môi trường node không có DOM.
 */

interface TransferItemLike {
  kind: string;
  type: string;
  getAsFile(): File | null;
}

export interface TransferLike {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<TransferItemLike> | null;
  types?: ArrayLike<string> | null;
}

const typesOf = (t: TransferLike | null | undefined) => Array.from(t?.types ?? []);

/**
 * Tệp cần đưa lên: tệp ảnh đầu tiên; không có ảnh mà có tệp khác thì trả tệp đó, để `checkImageFile` từ chối bằng đúng
 * lời ("chỉ nhận PNG, JPEG…") thay vì im lặng. `null` = trong đó không có tệp nào (chỉ có chữ hay đường link).
 */
export function fileFromTransfer(t: TransferLike | null | undefined): File | null {
  if (!t) return null;
  const files = Array.from(t.files ?? []);
  // Một số nguồn chỉ đưa tệp qua `items` (clipboard của vài trình duyệt), `files` thì rỗng.
  for (const item of Array.from(t.items ?? [])) {
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (file) files.push(file);
  }
  return files.find((f) => f.type.startsWith("image/")) ?? files[0] ?? null;
}

/**
 * Thứ đang được kéo có thể là ảnh: có tệp, hoặc có đường link. Lúc `dragover` trình duyệt chưa cho đọc tệp, chỉ cho xem
 * loại. Nhận cả link để khi thả mà không có tệp (ứng dụng chỉ đưa link) người dùng còn nghe được cách làm khác, thay vì
 * thả xuống mà không có gì xảy ra.
 */
export function transferMayHoldImage(t: TransferLike | null | undefined): boolean {
  const types = typesOf(t);
  return types.includes("Files") || types.includes("text/uri-list");
}

/** Đang kéo TỆP — chặn trình duyệt mở tệp đó thay cho trang khi người dùng thả hụt ra ngoài ô ảnh. */
export function transferHasFiles(t: TransferLike | null | undefined): boolean {
  return typesOf(t).includes("Files");
}

/**
 * Lần dán này có phải để đưa ảnh lên không.
 *
 * Đứng ở một ô chữ mà clipboard CÓ chữ thì để trình duyệt dán chữ: copy một ô Excel mang theo cả chữ lẫn ảnh PNG của ô đó,
 * dán vào ô Mô tả phải ra chữ chứ không phải đổi ảnh sản phẩm. Clipboard chỉ có ảnh (copy ảnh trong WeChat) thì dán ở đâu
 * trên form cũng là đổi ảnh.
 */
export function pasteIsImageUpload(t: TransferLike | null | undefined, targetIsTextEntry: boolean): boolean {
  if (!fileFromTransfer(t)) return false;
  return !(targetIsTextEntry && typesOf(t).includes("text/plain"));
}

/** Phím dán theo máy người dùng, để lời hướng dẫn nói đúng phím họ sẽ bấm. */
export function pasteShortcut(platform: string): string {
  return /Mac|iPhone|iPad|iPod/i.test(platform) ? "⌘V" : "Ctrl+V";
}

/**
 * MỌI tệp trong lần kéo thả / dán (bằng chứng đặt hàng và ảnh yêu cầu của khách nhận nhiều tệp một lần — yêu cầu v1.8 mục 2.3,
 * 4.3). Không trùng tệp khi trình duyệt đưa cùng một tệp qua cả `files` lẫn `items`.
 */
export function filesFromTransfer(t: TransferLike | null | undefined): File[] {
  if (!t) return [];
  const out: File[] = [...Array.from(t.files ?? [])];
  if (out.length === 0) {
    for (const item of Array.from(t.items ?? [])) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (file) out.push(file);
    }
  }
  return out;
}
